import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEye } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { Contract } from '@core/generated/gql-types';
import { useContractStore } from '@core/contract/useContractStore';
import { downloadFileFromUrl } from '@/utils/download.util';
import { useTenantId } from '@/contexts/TenantContext';
import DocumentPreviewModal from '@/components/worker/documents/DocumentPreviewModal';

interface ClientDocumentsProps {
  clientId: string;
  clientName?: string | null;
}

const ClientDocuments: React.FC<ClientDocumentsProps> = ({
  clientId,
  clientName,
}) => {
  const tenantId = useTenantId();
  const { fetchContractsByOwnerId, fetchContractById } = useContractStore();

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [previewContract, setPreviewContract] = useState<Contract | null>(null);

  useEffect(() => {
    const loadContracts = async () => {
      if (!clientId || !tenantId) {
        setContracts([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const clientContracts = await fetchContractsByOwnerId(
          clientId,
          tenantId,
        );
        setContracts(clientContracts);
      } catch (error) {
        console.error('Error loading client contracts:', error);
        setContracts([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadContracts();
  }, [clientId, tenantId, fetchContractsByOwnerId]);

  const formatContractDate = (dateString?: string | null): string => {
    if (!dateString) return '';
    try {
      const date = new Date(parseInt(dateString, 10));
      if (isNaN(date.getTime())) return dateString;
      return format(date, 'd MMM yyyy');
    } catch {
      return dateString;
    }
  };

  const getContractDownloadFilename = useCallback(
    (contract: Contract): string => {
      const safeClientName =
        clientName?.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '') ||
        'client';
      const datePart = contract.agreedAt
        ? format(
            new Date(
              /^\d+$/.test(contract.agreedAt)
                ? parseInt(contract.agreedAt, 10)
                : contract.agreedAt,
            ),
            'yyyy-MM-dd',
          )
        : contract.id;
      return `contract-${safeClientName}-${datePart}.pdf`;
    },
    [clientName],
  );

  const handlePreviewContract = useCallback(
    async (contract: Contract) => {
      try {
        const refreshedContract = await fetchContractById(contract.id);
        setPreviewContract(refreshedContract);
      } catch (error) {
        console.error('Error refreshing contract preview URL:', error);
        setPreviewContract(contract);
      }
    },
    [fetchContractById],
  );

  const handleDownloadContract = useCallback(
    async (contract: Contract) => {
      try {
        const refreshedContract = await fetchContractById(contract.id);
        if (!refreshedContract.pdfUrl) return;

        downloadFileFromUrl(
          refreshedContract.pdfUrl,
          getContractDownloadFilename(contract),
        );
      } catch (error) {
        console.error('Error refreshing contract download URL:', error);
        if (!contract.pdfUrl) return;

        downloadFileFromUrl(
          contract.pdfUrl,
          getContractDownloadFilename(contract),
        );
      }
    },
    [fetchContractById, getContractDownloadFilename],
  );

  if (isLoading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <div className="animate-pulse">
          <div className="mb-4 h-6 w-48 rounded bg-gray-200"></div>
          <div className="space-y-4">
            <div className="h-4 w-full rounded bg-gray-200"></div>
            <div className="h-4 w-full rounded bg-gray-200"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Contract</h3>
        {contracts.length === 0 ? (
          <div className="py-4 text-sm text-gray-500">
            No contract uploaded yet.
          </div>
        ) : (
          <div className="space-y-4">
            {contracts.map((contract) => (
              <div
                key={contract.id}
                className="flex items-center justify-between border-b border-gray-100 pb-4 last:border-b-0"
              >
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    Contract
                    {contract.isAgreedTo ? (
                      <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        Agreed
                      </span>
                    ) : (
                      <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        Pending
                      </span>
                    )}
                  </div>
                  {contract.isAgreedTo && contract.agreedAt && (
                    <div className="mt-1 text-xs text-gray-500">
                      Agreed on {formatContractDate(contract.agreedAt)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => void handlePreviewContract(contract)}
                    className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <FontAwesomeIcon icon={faEye} className="h-4 w-4" />
                    Preview
                  </button>
                  <button
                    onClick={() => void handleDownloadContract(contract)}
                    className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <DocumentPreviewModal
        contractPreview={{
          previewContract,
          setPreviewContract,
        }}
      />
    </div>
  );
};

export default ClientDocuments;
